/**
 * Pro page — the pure decisions the page and its tests share (design_handoff_pro_page/README.md).
 * Presentation lives in ProPage.jsx / ProBlocks.jsx; this file only answers "what goes where".
 */
import { openState, sinceMonth } from './vendor/vendorUi';
import { DEFAULT_RATIO } from '../event/coverLayout';

export const HOT_PINK = '#FF00AA';
export const GREEN = '#16A34A';
export const MUTED = '#6B7280';
export const FAINT = '#9CA3AF';

/**
 * Public tabs apply to existing and new pages, including desktop bookable pages. OFFERS is on
 * EVERY kind — a bookable pro posts offers from the same dashboard tab a place does, and the
 * owner switches the tab off in dashboard SETTINGS (hidden_tabs) if they don't want it.
 */
export const TABS = {
  bookable: ['about', 'events', 'photos', 'services', 'offers', 'faces'],
  eatery: ['about', 'events', 'menu', 'offers', 'hours', 'photos'],
  bar: ['about', 'events', 'drinks', 'offers', 'hours', 'photos'],
  venue: ['about', 'events', 'hire', 'offers', 'hours', 'photos'],
  shop: ['about', 'events', 'services', 'offers', 'hours', 'photos'],
};
export const MOBILE_TABS = {
  bookable: ['about', 'events', 'photos', 'services', 'offers', 'faces'],
  eatery: ['about', 'events', 'menu', 'offers', 'hours', 'where'],
  bar: ['about', 'events', 'drinks', 'offers', 'hours', 'where'],
  venue: ['events', 'hire', 'offers', 'hours', 'photos'],
  shop: ['about', 'events', 'services', 'offers', 'hours', 'where'],
};

export const KIND_LABEL = { bookable: 'BOOKABLE PRO', eatery: 'EATERY', bar: 'BAR', venue: 'VENUE', shop: 'SHOP' };

/**
 * The owner's tab order applied to a kind's built-in list: tabs named in `order` come first in
 * that order, anything the list carries that the order doesn't know keeps its built-in place
 * after them. Keys in `order` the list doesn't carry (a phone list without WHERE) are skipped.
 */
export function orderTabs(list, order) {
  const base = list || [];
  if (!order || !order.length) return base;
  const known = new Set(base);
  const ordered = order.filter((t) => known.has(t));
  const placed = new Set(ordered);
  return [...ordered, ...base.filter((t) => !placed.has(t))];
}

/** The tab a page opens on: the one asked for (a path segment) if visible, else the owner's default, else the first. */
export function resolveTab(tabs, requested, defaultTab) {
  if (!tabs || !tabs.length) return 'about';
  if (requested && tabs.includes(requested)) return requested;
  if (defaultTab && tabs.includes(defaultTab)) return defaultTab;
  return tabs[0];
}

/** The path a tab lives at: the default tab is the bare page, every other tab is its own segment. */
export function tabPath(slug, tab, tabs, defaultTab) {
  const home = resolveTab(tabs, null, defaultTab);
  return tab === home ? `/pro/${slug}` : `/pro/${slug}/${tab}`;
}

/** Trust line per kind. tone: green | plain | grey. */
export function trustLine(biz) {
  const kind = biz.kind || 'bookable';
  if (biz.isListed) {
    if (kind === 'bookable') return { text: '0 VERIFIED GIGS', note: 'NOT BOOKABLE YET', tone: 'grey' };
    if (kind === 'venue') return { text: 'AS POSTED', note: 'CAPACITY UNCONFIRMED', tone: 'grey' };
    return { text: 'AS POSTED', note: 'NOT YET ON PORTAL', tone: 'grey' };
  }
  if (kind === 'eatery' || kind === 'bar') {
    const st = openState(biz.hours);
    return { text: `ON PORTAL SINCE ${sinceMonth(biz.memberSince) || 'TODAY'}`, note: st ? st.label : null, tone: 'plain' };
  }
  if (kind === 'venue') return { text: `${biz.partiesHostedHere || 0} EVENT${biz.partiesHostedHere === 1 ? '' : 'S'} HOSTED HERE`, note: 'ESCROW-VERIFIED BOOKING', tone: 'green' };
  if (kind === 'shop') return { text: `${biz.verifiedGigCount || 0} VERIFIED BOOKING${biz.verifiedGigCount === 1 ? '' : 'S'}`, note: 'ESCROW-VERIFIED BOOKING', tone: 'green' };
  return { text: `${biz.verifiedGigCount || 0} VERIFIED GIG${biz.verifiedGigCount === 1 ? '' : 'S'}`, note: 'ESCROW-VERIFIED BOOKING', tone: 'green' };
}

export const trustColor = (tone) => (tone === 'green' ? GREEN : tone === 'plain' ? '#000' : FAINT);

/** The phone's 3-up stats strip: [value, label, colour?]. */
export function statsFor(biz, liveOfferCount = 0) {
  const kind = biz.kind || 'bookable';
  const photos = (biz.photos || []).length;
  const listed = !!biz.isListed;
  if (kind === 'eatery') return [[photos, 'PHOTOS'], [biz.priceBand ? '$'.repeat(biz.priceBand) : '—', 'PRICE BAND'], [sinceMonth(biz.memberSince) || '—', 'ON PR SINCE']];
  if (kind === 'venue') return [[biz.partiesHostedHere || 0, 'EVENTS HOSTED', GREEN], [biz.capacity || '—', 'CAPACITY'], [biz.ageMinimum ? `${biz.ageMinimum}+` : '—', 'AGE']];
  if (kind === 'bar') return [[photos, 'PHOTOS'], [liveOfferCount, 'OFFERS'], [sinceMonth(biz.memberSince) || '—', 'ON PR SINCE']];
  if (kind === 'shop') return [[biz.verifiedGigCount || 0, 'VERIFIED BOOKINGS', GREEN], [photos, 'PHOTOS'], [(biz.brands || '').split(',').filter((b) => b.trim()).length, 'BRANDS']];
  return [[listed ? 0 : (biz.verifiedGigCount || 0), 'VERIFIED GIGS', GREEN], [photos, 'PHOTOS'], [biz.team?.length || 0, 'FACES']];
}

/**
 * The cover document as the page keeps it: photos in cover order with their framing, the
 * saved layout / split / count and the card pick. `ratios` overlays measured shapes.
 */
export function coverFromBiz(biz, ratios = {}) {
  const photos = (biz?.photos || []).map((p, i) => ({
    id: p.id || `p${i}`, src: p.url, path: p.path, r: ratios[p.url] || DEFAULT_RATIO, measured: !!ratios[p.url],
    fx: num(p.focalX), fy: num(p.focalY),
  }));
  return {
    photos,
    layout: biz?.coverLayout || 'auto',
    split: [60, 50, 40].includes(Number(biz?.coverSplit)) ? Number(biz.coverSplit) : 60,
    count: Number(biz?.coverPhotoCount || 0),
    cardId: biz?.cardPhotoId || null,
  };
}

/** The photos that make the cover: the first `count` (all when unset). */
export function coverSlice(cover) {
  const n = cover.photos.length;
  if (!n) return [];
  const k = cover.count > 0 ? Math.min(cover.count, n) : n;
  return cover.photos.slice(0, k);
}

function num(v, fallback = 50) {
  const n = Number(v);
  return Number.isFinite(n) ? Math.max(0, Math.min(100, n)) : fallback;
}

/** Which blocks fill the main column and the rail for a kind + tab (README "Blocks by version / tab"). */
export function blocksFor(kind, tab, { phone = false } = {}) {
  let main = [];
  let rail = [];
  if (kind === 'bookable') {
    if (!phone && tab === 'about') { main = ['known', 'about', 'services', 'events', 'reviews', 'work']; rail = ['week', 'faces', 'flake', 'share']; }
    else main = { about: ['known', 'strip', 'about', 'reviews', 'share'], events: ['events'], photos: ['work'], services: ['services', 'week', 'flake'], offers: ['offers'], faces: ['faces'] }[tab] || ['about'];
  } else {
    const place = ['hours', 'where', 'attrs'];
    const aboutMain = { eatery: ['known', 'strip', 'about', 'reviews', 'asPosted'], bar: ['known', 'strip', 'about', 'whatson', 'happy', 'reviews', 'asPosted'], venue: ['events', 'about', 'reviews', 'asPosted'], shop: ['strip', 'about', 'shopServices', 'reviews', 'asPosted'] }[kind] || ['about'];
    const aboutRail = { eatery: [...place, 'share'], bar: [...place, 'share'], venue: ['hire', 'hours', 'where', 'share'], shop: ['brands', 'certs', ...place, 'share'] }[kind] || ['share'];
    const bodyTab = { menu: [['menu'], []], drinks: [['drinks'], ['snacks', 'happy', 'whatson']], hire: [['hire'], []], services: [['shopServices'], ['brands', 'certs']] };
    if (tab === 'about') { main = aboutMain; rail = phone ? ['attrs', 'share'] : aboutRail; }
    else if (bodyTab[tab]) { [main, rail] = bodyTab[tab]; }
    else if (tab === 'events') main = ['events'];
    else if (tab === 'offers') main = ['offers'];
    else if (tab === 'hours') { main = ['hours']; rail = phone ? [] : ['where', 'attrs']; }
    else if (tab === 'where') main = ['where', 'attrs'];
    else if (tab === 'photos') main = ['work'];
    else main = aboutMain;
  }
  if (phone) { main = [...main, ...rail]; rail = []; }
  return { main, rail };
}
